import { useCallback, useEffect, useMemo } from 'react';
import 'twin.macro';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import tw, { styled } from 'twin.macro';
import { DevicePhoneMobileIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';

import { ButtonV2, Title } from '@restworld/ui-ds';
import { useAuth } from '@restworld/feature-authentication';

import {
  candidateModalVariantAtom,
  useCandidateStateChangeModal,
  candidateStateChangeSelectionAtom,
  selectedJobApplicationAtom,
  isCandidateHiredSuccessfullyAtom,
  isCandidateModalSuccessScreenAtom,
} from '../../../../employer-job-listing.atoms';
import useUpdateJobApplication, {
  useUpdateDiscardedJobApplication,
} from '../../../../hooks/use-update-job-application';
import useGetOrganizationPerformanceMetricsQuery from '../../../../../organisations/hooks/use-get-organization-performance-metrics-query';

interface StatusChangeConfirmationProps {
  workerId: string;
  jobPositionId: string;
}
export const CandidateStateChangeConfirmation = ({ workerId, jobPositionId }: StatusChangeConfirmationProps) => {
  const { t } = useTranslation('job-positions-page');
  const { mutate, isSuccess, isPending } = useUpdateJobApplication();
  const { user } = useAuth();
  const [variant] = useAtom(candidateModalVariantAtom);
  const [selectedJobApplication] = useAtom(selectedJobApplicationAtom);
  const [, setIsCandidateHiredSuccessfullyAtom] = useAtom(isCandidateHiredSuccessfullyAtom);
  const [, setIsCandidateModalSuccessScreen] = useAtom(isCandidateModalSuccessScreenAtom);
  const candidateName = selectedJobApplication?.worker?.name ?? '';
  const employerName = user?.name ?? '';

  const {
    mutate: mutateDiscardApplication,
    isSuccess: isSuccessMutateDiscardApplication,
    isPending: isPendingMutateDiscardApplication,
  } = useUpdateDiscardedJobApplication();
  const statusKey = useAtomValue(candidateStateChangeSelectionAtom);
  const { data: performanceMetrics } = useGetOrganizationPerformanceMetricsQuery({
    enabled: statusKey === 'worker_jp_hired',
  });

  const defaultText = useMemo(
    () =>
      statusKey === 'worker_jp_hired'
        ? `${t('do_you_confirm_name_has_been_hired', { name: candidateName })}?`
        : statusKey === 'worker_jp_stage'
        ? t('you_are_moving_name_in_candidates_in_stage_or_negotiation', { name: candidateName })
        : t('you_are_moving_name_in_candidates_to_be_called', { name: candidateName }),
    [candidateName, statusKey, t]
  );

  const successText = useMemo(
    () =>
      statusKey === 'worker_jp_hired' ? (
        <span>
          {t('congratulations')} {employerName}!<br />
          {t('this_is_your_x_hire_with_restworld', { count: performanceMetrics?.hired_workers })}
        </span>
      ) : statusKey === 'worker_jp_stage' ? (
        `${t('you_moved_name_in_stage_or_negotiation', { name: candidateName })}.`
      ) : (
        t('you_moved_name_in_candidates_to_be_called', { name: candidateName })
      ),
    [candidateName, employerName, performanceMetrics?.hired_workers, statusKey, t]
  );

  const handleSubmit = useCallback(() => {
    const params = {
      params: {
        status_key: statusKey !== '' ? statusKey : null,
      },
      workerId,
      jobPositionId,
    };

    if (
      (statusKey !== 'worker_jp_employer_interested' &&
        statusKey !== 'worker_jp_stage' &&
        statusKey !== 'worker_jp_hired') ||
      variant === 'worker_jp_recovery_confirmation'
    ) {
      mutateDiscardApplication(params);
      return;
    }
    mutate(params);
  }, [jobPositionId, mutate, mutateDiscardApplication, statusKey, variant, workerId]);

  useEffect(() => {
    if (statusKey === 'worker_jp_hired' && isSuccess) {
      setIsCandidateHiredSuccessfullyAtom(true);
    }
    if (isSuccess) setIsCandidateModalSuccessScreen(true);
  }, [isSuccess, setIsCandidateHiredSuccessfullyAtom, setIsCandidateModalSuccessScreen, statusKey]);

  return (
    <CandidateModalStatusChangeScreen
      topIcon={
        statusKey === 'worker_jp_stage' ? (
          <ClipboardDocumentCheckIcon tw="h-6 w-6 text-neutral-700" />
        ) : statusKey === 'worker_jp_hired' && isSuccess ? (
          '🎉🎉🎉'
        ) : statusKey === 'worker_jp_hired' ? null : (
          <DevicePhoneMobileIcon tw="h-6 w-6 text-neutral-700" />
        )
      }
      successText={successText}
      defaultText={defaultText}
      isCompletedSuccessfully={isSuccess || isSuccessMutateDiscardApplication}
      isLoading={isPending || isPendingMutateDiscardApplication}
      handleSubmit={handleSubmit}
      hideBottomText={statusKey === 'worker_jp_hired' && !isSuccess}
      subtext={
        statusKey === 'worker_jp_hired' && isSuccess ? `${t('let_us_know_how_it_goes_and_if_you_need_more_help')}!` : ''
      }
      hasPurpleCta={statusKey === 'worker_jp_hired' && isSuccess}
    />
  );
};

export function CandidateModalStatusChangeScreen({
  handleSubmit,
  isCompletedSuccessfully,
  isLoading,
  successText,
  defaultText,
  subtext,
  topIcon,
  hideBottomText,
  hasPurpleCta,
  bottomConfirmationText,
}: CandidateModalStatusChangeScreenProps) {
  const { t } = useTranslation('job-positions-page');
  const { closeCandidateModal } = useCandidateStateChangeModal();
  const [isCandidateModalSuccessScreen] = useAtom(isCandidateModalSuccessScreenAtom);
  const [, setCandidateModalVariant] = useAtom(candidateModalVariantAtom);

  const handleCancel = useCallback(() => setCandidateModalVariant('default'), [setCandidateModalVariant]);

  return (
    <div tw="flex flex-col h-full">
      <ContentWrapper isSuccess={isCandidateModalSuccessScreen}>
        <>
          <div tw="self-stretch flex-col justify-start items-center gap-5 flex">
            {topIcon}
            <Title isCenter fontWeight="semibold" fontSize="large" textColor="neutral-700">
              {isCompletedSuccessfully ? successText : defaultText}
            </Title>
            {subtext && (
              <Title fontWeight="medium" fontSize="base" textColor="neutral-200">
                {subtext}
              </Title>
            )}
            {hideBottomText ? null : isCompletedSuccessfully ? (
              <ButtonV2
                onClick={() => {
                  handleCancel();
                  closeCandidateModal();
                }}
                variant={hasPurpleCta ? 'secondary-restworld' : 'primary'}
              >
                {t('close')}
              </ButtonV2>
            ) : (
              <Title isCenter fontWeight="medium" fontSize="base" textColor="neutral-200">
                {bottomConfirmationText ?? `${t('do_you_confirm_your_choice')}?`}
              </Title>
            )}
          </div>
        </>
      </ContentWrapper>

      {!isCompletedSuccessfully && (
        <div tw="shrink-0 flex justify-between gap-[15px] py-4 border-t border-neutral-40 -mx-5 px-5 lg:(-mx-10 px-10)">
          <ButtonV2 type="button" variant="tertiary" onClick={handleCancel}>
            {t('cancel')}
          </ButtonV2>
          <ButtonV2 loading={isLoading} onClick={handleSubmit} twStyle={tw`min-h-fit`}>
            {t('confirm')}
          </ButtonV2>
        </div>
      )}
    </div>
  );
}

interface CandidateModalStatusChangeScreenProps {
  isCompletedSuccessfully?: boolean;
  isLoading?: boolean;
  handleSubmit?: () => void;
  defaultText?: string;
  successText?: React.ReactNode;
  subtext?: string;
  topIcon?: React.ReactNode;
  hideBottomText?: boolean;
  hasPurpleCta?: boolean;
  bottomConfirmationText?: React.ReactNode;
}

const ContentWrapper = styled.div<{ isSuccess?: boolean }>`
  ${tw`flex flex-col justify-center items-center flex-1 min-h-0 w-full overflow-y-auto lg:(relative)`}
`;
